import { useGlobalContext } from 'apps/student/src/app/Context/GlobalContextProvider';
import { SetStateAction, useCallback, useEffect } from 'react';

interface UsePalModePopupParams {
  currentPalModeKey: string;
  uniqueId?: string;
  shouldCompareUniqueId?: boolean;
}

export const usePalModePopup = ({
  currentPalModeKey,
  uniqueId,
  shouldCompareUniqueId = true,
}: UsePalModePopupParams) => {
  const {
    showPalModePopup,
    setShowPalModePopup,
    palLoadingStatus,
    setPalLoadingStatus,
  } = useGlobalContext();

  const isWarmup = currentPalModeKey === 'warmup';

  useEffect(() => {
    if (isWarmup) {
      setShowPalModePopup(false);
      return;
    }
    if (!uniqueId) return;

    const hasModeChanged = palLoadingStatus.mode !== currentPalModeKey;
    const hasUniqueIdChanged =
      shouldCompareUniqueId && palLoadingStatus.uniqueId !== uniqueId;

    if (hasModeChanged || hasUniqueIdChanged) {
      setShowPalModePopup(true);
    }
  }, [
    isWarmup,
    currentPalModeKey,
    uniqueId,
    shouldCompareUniqueId,
    palLoadingStatus.mode,
    palLoadingStatus.uniqueId,
    setShowPalModePopup,
  ]);

  useEffect(() => {
    return () => {
      setPalLoadingStatus({
        uniqueId: undefined,
        mode: undefined,
      });
    };
  }, [setPalLoadingStatus]);

  const onPalModePopupOpenChange = useCallback(
    (value: SetStateAction<boolean>) => {
      const nextOpen =
        typeof value === 'function' ? value(showPalModePopup) : value;
      setShowPalModePopup(nextOpen);
      if (!nextOpen) {
        setPalLoadingStatus({
          uniqueId,
          mode: currentPalModeKey,
        });
      }
    },
    [
      currentPalModeKey,
      showPalModePopup,
      uniqueId,
      setPalLoadingStatus,
      setShowPalModePopup,
    ]
  );

  return {
    showPalModePopup: isWarmup ? false : showPalModePopup,
    onPalModePopupOpenChange,
  };
};
